import { createReducer, on } from '@ngrx/store';
import * as IncomingTopicsV2Actions from './actions';
import {
  IncomingTopicsDto,
  IncomingTopicsSto,
  IncomingTopicTileDto,
  RelatedIncomingTopicTypeSto,
  RelatedObjectSto,
} from '../models/incoming-topic.model';

export interface IncomingTopicsPageResult {
  loadedAt: Date | null;
  totalCount: number;
  topics: IncomingTopicsSto[];
  related: RelatedObjectSto[];
  limit: number;
  offset: number;
}

export interface IncomingTopicsV2State {
  pageLoading: Record<string, boolean>;
  pageResult: Record<string, IncomingTopicsPageResult>;
  detailsLoading: boolean;
  detailsLoadedAt: Date | null;
  detailsResult: {
    topic: IncomingTopicsSto | null;
    related: RelatedObjectSto[];
  } | null;
  tilesLoading: boolean;
  tilesResult: {
    tiles: IncomingTopicTileDto[];
    types: RelatedIncomingTopicTypeSto[];
  };
}

const initialState: IncomingTopicsV2State = {
  pageLoading: {},
  pageResult: {},
  detailsLoading: false,
  detailsLoadedAt: null,
  detailsResult: null,
  tilesLoading: false,
  tilesResult: {
    tiles: [],
    types: [],
  },
};

export const reducer = createReducer(
  initialState,

  on(IncomingTopicsV2Actions.loadIncomingTopicsPage, (state, { viewId }) => ({
    ...state,
    pageLoading: { ...state.pageLoading, [viewId]: true },
  })),

  on(IncomingTopicsV2Actions.loadIncomingTopicsPageFailed, (state, { viewId }) => ({
    ...state,
    pageLoading: { ...state.pageLoading, [viewId]: false },
  })),

  on(IncomingTopicsV2Actions.loadIncomingTopicsPageSuccessful, (state, { viewId, topics, topicsTotalCount, related, query }) => ({
    ...state,
    pageLoading: { ...state.pageLoading, [viewId]: false },
    pageResult: {
      ...state.pageResult,
      [viewId]: {
        loadedAt: new Date(),
        totalCount: topicsTotalCount,
        topics,
        related,
        limit: query.limit,
        offset: query.offset,
      },
    },
  })),

  on(IncomingTopicsV2Actions.loadIncomingTopicDetails, state => ({
    ...state,
    detailsLoading: true,
    detailsLoadedAt: null,
    detailsResult: null,
  })),

  on(IncomingTopicsV2Actions.loadIncomingTopicDetailsFailed, state => ({
    ...state,
    detailsLoading: false,
    detailsLoadedAt: null,
    detailsResult: null,
  })),

  on(IncomingTopicsV2Actions.loadIncomingTopicDetailsSuccessful, (state, { topic, related }) => ({
    ...state,
    detailsLoading: false,
    detailsLoadedAt: new Date(),
    detailsResult: { topic, related },
  })),

  on(IncomingTopicsV2Actions.changeIncomingTopicStatus, (state, { aggregateId, status }) => {
    const updatedPageResult: Record<string, IncomingTopicsPageResult> = {};
    for (const viewId of Object.keys(state.pageResult)) {
      const page = state.pageResult[viewId];
      updatedPageResult[viewId] = {
        ...page,
        topics: page.topics.map(t =>
          t.aggregateId === aggregateId ? Object.assign(new IncomingTopicsSto({} as IncomingTopicsDto), t, { status }) : t
        ),
      };
    }

    const updatedDetailsResult =
      state.detailsResult?.topic?.aggregateId === aggregateId
        ? {
            ...state.detailsResult,
            topic: Object.assign(new IncomingTopicsSto({} as IncomingTopicsDto), state.detailsResult.topic, { status }),
          }
        : state.detailsResult;

    return {
      ...state,
      pageResult: updatedPageResult,
      detailsResult: updatedDetailsResult,
    };
  }),

  on(IncomingTopicsV2Actions.assignResourceSuccessful, (state, { aggregateId, assignedResourceId }) => {
    const updatedPageResult: Record<string, IncomingTopicsPageResult> = {};
    for (const viewId of Object.keys(state.pageResult)) {
      const page = state.pageResult[viewId];
      updatedPageResult[viewId] = {
        ...page,
        topics: page.topics.map(t =>
          t.aggregateId === aggregateId ? Object.assign(new IncomingTopicsSto({} as IncomingTopicsDto), t, { assignedResourceId }) : t
        ),
      };
    }

    const updatedDetailsResult =
      state.detailsResult?.topic?.aggregateId === aggregateId
        ? {
            ...state.detailsResult,
            topic: Object.assign(new IncomingTopicsSto({} as IncomingTopicsDto), state.detailsResult.topic, { assignedResourceId }),
          }
        : state.detailsResult;

    return {
      ...state,
      pageResult: updatedPageResult,
      detailsResult: updatedDetailsResult,
    };
  }),

  on(IncomingTopicsV2Actions.patchDueAtSuccessful, (state, { aggregateId, dueAt }) => {
    const updatedPageResult: Record<string, IncomingTopicsPageResult> = {};
    for (const viewId of Object.keys(state.pageResult)) {
      const page = state.pageResult[viewId];
      updatedPageResult[viewId] = {
        ...page,
        topics: page.topics.map(t =>
          t.aggregateId === aggregateId ? Object.assign(new IncomingTopicsSto({} as IncomingTopicsDto), t, { dueAt }) : t
        ),
      };
    }

    const updatedDetailsResult =
      state.detailsResult?.topic?.aggregateId === aggregateId
        ? {
            ...state.detailsResult,
            topic: Object.assign(new IncomingTopicsSto({} as IncomingTopicsDto), state.detailsResult.topic, { dueAt }),
          }
        : state.detailsResult;

    return {
      ...state,
      pageResult: updatedPageResult,
      detailsResult: updatedDetailsResult,
    };
  }),

  on(IncomingTopicsV2Actions.loadTiles, state => ({
    ...state,
    tilesLoading: true,
  })),

  on(IncomingTopicsV2Actions.loadTilesSuccessful, (state, { tiles, types }) => ({
    ...state,
    tilesLoading: false,
    tilesResult: {
      tiles,
      types,
    },
  })),

  on(IncomingTopicsV2Actions.loadTilesFailed, state => ({
    ...state,
    tilesLoading: false,
  }))
);

export const pageLoading = (state: IncomingTopicsV2State) => state?.pageLoading;
export const pageResult = (state: IncomingTopicsV2State) => state?.pageResult;
export const detailsLoading = (state: IncomingTopicsV2State) => state?.detailsLoading;
export const detailsLoadedAt = (state: IncomingTopicsV2State) => state?.detailsLoadedAt;
export const detailsResult = (state: IncomingTopicsV2State) => state?.detailsResult;
export const tilesLoading = (state: IncomingTopicsV2State) => state?.tilesLoading;
export const tilesResult = (state: IncomingTopicsV2State) => state?.tilesResult;
